import { Button, Card, Select, useToast } from "@design";
import type { UseQueryOptions } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { UserSearchResult } from "@/api";
import { errorMessage } from "@/api";
import { Text } from "@/components/Text";
import { AddTeamRow, MemberRow, ROLES, TeamMemberRow } from "./MembershipRows";
import { type SpaceGrant, SpaceGrantsSection } from "./SpaceGrantsSection";
import { UserSearchCombobox } from "./UserSearchCombobox";

/** One user member row, normalized across entities (agents, brands, micro-apps). */
export interface MemberUser {
  uuid: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  role: string;
}
/** One team member row, normalized across entities. */
export interface MemberTeam {
  uuid: string;
  name: string;
  role: string;
}

export interface MembershipData {
  canManage: boolean;
  userMembers: MemberUser[];
  teamMembers: MemberTeam[];
  /** Agents, data sources and apps: spaces whose members can use it (admins only). */
  spaceGrants: SpaceGrant[];
}

/** The `{ data: …MembersResponse }` shape every entity's GET …/members returns. */
interface MembersEnvelope {
  data: {
    canManage: boolean;
    userMembers?: Array<{
      role: string;
      user: { uuid: string; name?: string | null; email: string; avatarUrl?: string | null };
    }>;
    teamMembers?: Array<{ teamUuid: string; teamName: string; role: string }>;
    spaceGrants?: SpaceGrant[];
  };
}

/** Normalize a members response (agents, brands, micro-apps all share the shape). */
export function parseMembers(response: unknown): MembershipData {
  const d = (response as MembersEnvelope).data;
  return {
    canManage: d.canManage,
    userMembers: (d.userMembers ?? []).map((m) => ({
      uuid: m.user.uuid,
      name: m.user.name ?? m.user.email,
      email: m.user.email,
      avatarUrl: m.user.avatarUrl,
      role: m.role,
    })),
    teamMembers: (d.teamMembers ?? []).map((tm) => ({ uuid: tm.teamUuid, name: tm.teamName, role: tm.role })),
    spaceGrants: d.spaceGrants ?? [],
  };
}

/**
 * A generic adapter over an entity's membership endpoints, all of which share
 * the same shape (`GET …/members`, `POST/PATCH/DELETE …/members/users|teams`).
 */
export interface MembershipApi {
  // biome-ignore lint/suspicious/noExplicitAny: the generated query options carry entity-specific error/data types; the panel only reads `data` opaquely and passes it to `parse`.
  membersOptions: () => UseQueryOptions<any, any, any, any>;
  membersQueryKey: () => readonly unknown[];
  addUser: (id: string, role: string) => Promise<unknown>;
  updateUserRole: (id: string, role: string) => Promise<unknown>;
  removeUser: (id: string) => Promise<unknown>;
  /** Team membership (role edit + remove). Optional until an entity wires it. */
  addTeam?: (id: string, role: string) => Promise<unknown>;
  updateTeamRole?: (id: string, role: string) => Promise<unknown>;
  removeTeam?: (id: string) => Promise<unknown>;
  /** Override only if an entity's members response differs; defaults to `parseMembers`. */
  parse?: (data: unknown) => MembershipData;
}

/**
 * The membership-with-roles Access panel (agents, brands, micro-apps). The
 * per-entity work is a `MembershipApi` adapter; the panel renders people +
 * teams, add/remove, and role selects. The visibility-flag model (skills,
 * data sources) is a separate variant — unify when a second entity needs it.
 */
export function MembershipPanel({ api, currentUserUuid }: Readonly<{ api: MembershipApi; currentUserUuid?: string }>) {
  const { t } = useTranslation("build");
  const toast = useToast();
  const queryClient = useQueryClient();
  const members = useQuery(api.membersOptions());
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [role, setRole] = useState<string>("viewer");
  const [addKey, setAddKey] = useState(0);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: api.membersQueryKey() });
  const onError = (error: unknown) => toast.error(errorMessage(error, t("access.addFailed")));

  const add = useMutation({
    mutationFn: () => api.addUser((selectedUser as UserSearchResult).uuid, role),
    onSuccess: async () => {
      await invalidate();
      setSelectedUser(null);
      // The combobox holds its own text; clear by remounting via key.
      setAddKey((k) => k + 1);
      toast.success(t("access.added"));
    },
    onError,
  });
  const changeRole = useMutation({
    mutationFn: ({ id, r }: { id: string; r: string }) => api.updateUserRole(id, r),
    onSuccess: invalidate,
    onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.removeUser(id), onSuccess: invalidate, onError });
  const changeTeamRole = useMutation({
    mutationFn: ({ id, r }: { id: string; r: string }) => {
      if (!api.updateTeamRole) return Promise.reject(new Error("Team editing not supported"));
      return api.updateTeamRole(id, r);
    },
    onSuccess: invalidate,
    onError,
  });
  const removeTeam = useMutation({
    mutationFn: (id: string) => {
      if (!api.removeTeam) return Promise.reject(new Error("Team editing not supported"));
      return api.removeTeam(id);
    },
    onSuccess: invalidate,
    onError,
  });
  const addTeam = useMutation({
    mutationFn: ({ id, r }: { id: string; r: string }) => {
      if (!api.addTeam) return Promise.reject(new Error("Team add not supported"));
      return api.addTeam(id, r);
    },
    onSuccess: invalidate,
    onError,
  });

  const data = members.data ? (api.parse ?? parseMembers)(members.data) : undefined;
  const canManage = data?.canManage ?? false;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        {/* People */}
        <div className="flex flex-col gap-3">
          <Text variant="body">{t("access.people")}</Text>
          <ul className="flex flex-col divide-y divide-border-subtle">
            {(data?.userMembers ?? []).map((m) => (
              <MemberRow
                key={m.uuid}
                member={m}
                isYou={m.uuid === currentUserUuid}
                canManage={canManage}
                onRole={(next) => changeRole.mutate({ id: m.uuid, r: next })}
                onRemove={() => remove.mutate(m.uuid)}
              />
            ))}
          </ul>
          {canManage && (
            <div className="flex items-center gap-2">
              <UserSearchCombobox
                key={addKey}
                excludeUuids={(data?.userMembers ?? []).map((m) => m.uuid)}
                onSelect={setSelectedUser}
              />
              <div className="w-32">
                <Select
                  size="md"
                  aria-label={t("access.role.label")}
                  options={ROLES.map((r) => ({ value: r, label: t(`access.role.${r}`) }))}
                  value={role}
                  onChange={setRole}
                />
              </div>
              <Button
                variant="secondary"
                size="md"
                onClick={() => add.mutate()}
                loading={add.isPending}
                disabled={!selectedUser?.uuid.includes("-")}
              >
                {t("access.addPerson")}
              </Button>
            </div>
          )}
        </div>

        {/* Teams */}
        <div className="flex flex-col gap-3 border-t border-border-subtle pt-4">
          <Text variant="body">{t("access.teams")}</Text>
          {(data?.teamMembers.length ?? 0) > 0 ? (
            <ul className="flex flex-col divide-y divide-border-subtle">
              {data?.teamMembers.map((tm) => (
                <TeamMemberRow
                  key={tm.uuid}
                  member={tm}
                  canManage={canManage && Boolean(api.updateTeamRole)}
                  onRole={(next) => changeTeamRole.mutate({ id: tm.uuid, r: next })}
                  onRemove={() => removeTeam.mutate(tm.uuid)}
                />
              ))}
            </ul>
          ) : (
            <Text variant="body-sm" tone="tertiary">
              {t("access.noTeams")}
            </Text>
          )}
          {canManage && api.addTeam && (
            <AddTeamRow
              excludeUuids={(data?.teamMembers ?? []).map((m) => m.uuid)}
              onAdd={(teamUuid, role) => addTeam.mutate({ id: teamUuid, r: role })}
              adding={addTeam.isPending}
            />
          )}
        </div>

        {canManage && <SpaceGrantsSection grants={data?.spaceGrants ?? []} onChanged={invalidate} />}

        {!canManage && (
          <Text variant="caption" tone="tertiary">
            {t("access.readOnly")}
          </Text>
        )}
      </div>
    </Card>
  );
}
